import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import leadGenerationImage from "@/assets/images/homepage/ai_lead_generation.webp";
import leadEnrichmentImage from "@/assets/images/homepage/lead_enrichment.webp";
import smartOutreachImage from "@/assets/images/homepage/smart_outreach.webp";
import campaignAnalyticsImage from "@/assets/images/homepage/campaign_analytics.webp";

const slides = [
  {
    id: 1,
    title: "AI Lead Generation",
    desc: "Discover targeted prospects based on industry, job role, location, company size, and business requirements.",
    image: leadGenerationImage,
  },
  {
    id: 2,
    title: "Lead Enrichment",
    desc: "Access verified emails, contact details, company insights, and decision-maker information for every prospect.",
    image: leadEnrichmentImage,
  },
  {
    id: 3,
    title: "Smart Outreach",
    desc: "Send personalized emails and automated follow-ups that help your team start more meaningful conversations.",
    image: smartOutreachImage,
  },
  {
    id: 4,
    title: "Campaign Analytics",
    desc: "Monitor deliveries, opens, replies, and campaign performance to continuously improve your outreach results.",
    image: campaignAnalyticsImage,
  },
];

export default function AuraSliderSection() {
  const [active, setActive] = useState(0);

  const handlePrev = () => {
    setActive((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActive((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const current = slides[active];
  const next = slides[(active + 1) % slides.length];

  return (
    <section className="w-full py-24 max-md:py-12 bg-[#f8f8fc] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 max-md:gap-3 items-center">
          {/* LEFT CONTENT */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium mb-6">
              <Sparkles size={16} />
              Powered by AI
            </div>

            <h2 className="text-5xl max-md:text-[27px] max-md:text-center md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              AI Agents That Work With You—and For You
            </h2>

            <p className="text-lg max-md:text-base max-md:text-center text-gray-600 leading-relaxed mb-8 max-md:mb-2 max-w-lg">
              Meet your always-on AI team, built to handle repetitive
              tasks so you can focus on growing your business.
            </p>

            <div className="hidden lg:flex gap-4">
              <button
                onClick={handlePrev}
                // disabled={!canGoPrev}
                className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center disabled:opacity-30"
              >
                <ChevronLeft size={20} />
              </button>

              <button
                onClick={handleNext}
                // disabled={!canGoNext}
                className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center disabled:opacity-30"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {/* RIGHT SLIDER */}
          <div className="relative h-[520px]  hidden lg:flex items-center">
            <AnimatePresence mode="popLayout">
              {/* ACTIVE CARD */}
              <motion.div
                key={current.id}
                layout
                initial={{ x: 0, scale: 1, opacity: 1 }}
                animate={{ x: 0, scale: 1, opacity: 1 }}
                exit={{
                  x: -100,
                  scale: 0.7,
                  opacity: 0,
                  transition: { duration: 0.5, ease: "easeInOut" },
                }}
                transition={{
                  layout: { duration: 0.5, ease: "easeInOut" },
                }}
                className="absolute left-0 w-[56%] rounded-3xl overflow-hidden bg-white shadow-2xl z-20"
              >
                <img
                  src={current.image}
                  alt={current.title}
                  className="w-full h-[280px]  object-cover"
                />

                <div className="p-7">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {current.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {current.desc}
                  </p>
                </div>
              </motion.div>

              {/* PREVIEW CARD */}
              {next && (
                <motion.div
                  key={next.id}
                  layout
                  initial={{ x: 100, scale: 0.7, opacity: 0 }}
                  animate={{ x: 0, scale: 0.86, opacity: 0.45 }}
                  exit={{
                    x: -100,
                    scale: 0.7,
                    opacity: 0,
                    transition: { duration: 0.3 },
                  }}
                  transition={{
                    layout: { duration: 0.5, ease: "easeInOut" },
                  }}
                  className="absolute right-0 w-[38%] rounded-3xl overflow-hidden bg-white shadow-xl z-10"
                >
                  <img
                    src={next.image}
                    alt={next.title}
                    className="w-full h-[220px] object-cover"
                  />

                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {next.title}
                    </h3>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* MOBILE */}
          {/* MOBILE */}
          <div className="lg:hidden">
            <div className="rounded-2xl overflow-hidden bg-white shadow-xl">
              <img
                src={current.image}
                alt={current.title}
                className="w-full h-[240px] object-cover max-md:object-fill"
              />
              <div className="px-2 py-4 text-center">
                <h3 className="text-2xl font-bold mb-4 ">{current.title}</h3>
                <p className="text-gray-600 text-sm">{current.desc}</p>
              </div>
            </div>

            {/* Mobile navigation below slider */}
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={handlePrev}
                className="w-11 h-11 rounded-full bg-black text-white flex items-center justify-center"
              >
                <ChevronLeft size={18} />
              </button>

              <button
                onClick={handleNext}
                className="w-11 h-11 rounded-full bg-black text-white flex items-center justify-center"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
